import {
  AgentSummary,
  FlightPriceAlertMatchResponse,
  ProactiveAgentListResponse,
} from "@/types/agent-summary"
import { DayObject } from "@/types/date"

import { dayObjectAsUrlParam } from "@/lib/date-utils"
import { PagingMeta } from "@/lib/paging"
import type { AppDispatch } from "@/lib/storage/redux-store"
import { baseApi } from "./base-api"

export const SETTINGS_USER_CREATED_AGENTS_QUERY = {
  created_by: "user_created" as const,
  per_page: 200,
}

// Watched searches can create ten child alerts. Fetch the controller's maximum
// so a normal settings view never splits one search across pagination pages and
// reports a partial hotel count.
export const SETTINGS_AUTO_CREATED_AGENTS_PER_PAGE = 200

export const settingsAutoCreatedAgentsQuery = (page = 1) => ({
  created_by: "auto_created" as const,
  page,
  per_page: SETTINGS_AUTO_CREATED_AGENTS_PER_PAGE,
})

export type ListProactiveAgentsParams = {
  created_by: "user_created" | "auto_created"
  page?: number
  per_page?: number
}

function mergeAgentsById(
  agents: AgentSummary[],
  nextAgents: AgentSummary[]
): AgentSummary[] {
  const byId = new Map<number, AgentSummary>()
  for (const agent of agents) byId.set(agent.id, agent)
  for (const agent of nextAgents) byId.set(agent.id, agent)
  return Array.from(byId.values())
}

function listCacheArgVariants(): ListProactiveAgentsParams[] {
  return [SETTINGS_USER_CREATED_AGENTS_QUERY, settingsAutoCreatedAgentsQuery(1)]
}

export const proactiveAgentsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getProactiveAgent: builder.query<AgentSummary, number>({
      query: (id) => ({ url: `/api/proactive_agents/${id}` }),
      providesTags: ["ProactiveAgent"],
    }),
    getProactiveAgents: builder.query<AgentSummary[], void>({
      query: () => ({ url: "/api/proactive_agents" }),
      transformResponse: (response: ProactiveAgentListResponse) =>
        response.proactive_agents,
      providesTags: ["ProactiveAgent"],
      keepUnusedDataFor: Infinity,
    }),
    listProactiveAgents: builder.query<
      ProactiveAgentListResponse,
      ListProactiveAgentsParams
    >({
      query: ({ created_by, page = 1, per_page }) => ({
        url: "/api/proactive_agents",
        params: { created_by, page, per_page },
      }),
      providesTags: ["ProactiveAgent"],
      serializeQueryArgs: ({ queryArgs }) => queryArgs.created_by,
      merge: (current, incoming, { arg }) => {
        if (
          arg.created_by === "auto_created" &&
          arg.page &&
          arg.page > 1 &&
          current
        ) {
          return {
            ...incoming,
            proactive_agents: mergeAgentsById(
              current.proactive_agents,
              incoming.proactive_agents
            ),
          }
        }
        return incoming
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg?.page !== previousArg?.page
      },
    }),
    createFlightPriceAlertFromQuote: builder.mutation<
      AgentSummary[],
      { quoteRef: string; watchThisFlightOnly: boolean }
    >({
      query: ({ quoteRef, watchThisFlightOnly }) => ({
        url: watchThisFlightOnly
          ? "/api/proactive_agents/flight_price_alerts/from_quote"
          : "/api/proactive_agents/flight_price_alerts/from_search",
        method: "POST",
        data: watchThisFlightOnly
          ? { quote_ref: quoteRef, watch_this_flight_only: true }
          : { quote_ref: quoteRef },
      }),
      transformResponse: (
        response: AgentSummary | { proactive_agents: AgentSummary[] }
      ): AgentSummary[] =>
        "proactive_agents" in response ? response.proactive_agents : [response],
      invalidatesTags: ["ProactiveAgent", "FlightPriceAlertMatch"],
    }),
    getFlightPriceAlertMatch: builder.query<
      FlightPriceAlertMatchResponse,
      { quoteRef: string }
    >({
      query: ({ quoteRef }) => ({
        url: "/api/proactive_agents/flight_price_alerts/match",
        params: { quote_ref: quoteRef },
      }),
      providesTags: ["FlightPriceAlertMatch"],
    }),
    deleteProactiveAgent: builder.mutation<void, { id: number }>({
      query: ({ id }) => ({
        url: `/api/proactive_agents/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["ProactiveAgent", "FlightPriceAlertMatch"],
    }),
    deleteProactiveAgentGroup: builder.mutation<
      void,
      { sourceIntentKey: string }
    >({
      query: ({ sourceIntentKey }) => ({
        url: `/api/proactive_agent_groups/${encodeURIComponent(sourceIntentKey)}`,
        method: "DELETE",
      }),
      invalidatesTags: ["ProactiveAgent", "FlightPriceAlertMatch"],
    }),
  }),
})

export const {
  useCreateFlightPriceAlertFromQuoteMutation,
  useDeleteProactiveAgentMutation,
  useDeleteProactiveAgentGroupMutation,
  useGetFlightPriceAlertMatchQuery,
  useGetProactiveAgentsQuery,
  useLazyGetProactiveAgentQuery,
  useListProactiveAgentsQuery,
} = proactiveAgentsApi

// Optimistically patch the shared agents cache, then invalidate the tag so
// any subscribed views reconcile with the server. updateQueryData alone
// no-ops when the cache is cold (never fetched) and is silently lost when a
// fetch was already in flight when the mutation landed.
export function patchProactiveAgentsCache(
  dispatch: (action: unknown) => unknown,
  recipe: (draft: AgentSummary[]) => void
) {
  dispatch(
    proactiveAgentsApi.util.updateQueryData(
      "getProactiveAgents",
      undefined,
      recipe
    )
  )
  dispatch(proactiveAgentsApi.util.invalidateTags(["ProactiveAgent"]))
}

export function patchProactiveAgentInListCaches(
  dispatch: AppDispatch,
  agentId: number,
  recipe: (agent: AgentSummary) => AgentSummary
): { undo: () => void } {
  const undos: Array<() => void> = []
  for (const args of listCacheArgVariants()) {
    const result = dispatch(
      proactiveAgentsApi.util.updateQueryData(
        "listProactiveAgents",
        args,
        (draft) => {
          const idx = draft.proactive_agents.findIndex((a) => a.id === agentId)
          if (idx >= 0)
            draft.proactive_agents[idx] = recipe(draft.proactive_agents[idx])
        }
      )
    )
    if (result.undo) undos.push(result.undo)
  }
  return { undo: () => undos.forEach((undo) => undo()) }
}

export function removeProactiveAgentFromListCaches(
  dispatch: AppDispatch,
  agent: AgentSummary
): { undo: () => void } {
  const undos: Array<() => void> = []
  for (const args of listCacheArgVariants()) {
    const result = dispatch(
      proactiveAgentsApi.util.updateQueryData(
        "listProactiveAgents",
        args,
        (draft) => {
          draft.proactive_agents = draft.proactive_agents.filter(
            (a) => a.id !== agent.id
          )
          if (args.created_by === "auto_created" && draft.meta) {
            draft.meta = decrementMetaTotal(draft.meta)
          }
        }
      )
    )
    if (result.undo) undos.push(result.undo)
  }
  return { undo: () => undos.forEach((undo) => undo()) }
}

export function restoreProactiveAgentInListCaches(
  dispatch: AppDispatch,
  agent: AgentSummary
) {
  const args =
    agent.created_by === "auto_created"
      ? settingsAutoCreatedAgentsQuery(1)
      : SETTINGS_USER_CREATED_AGENTS_QUERY

  dispatch(
    proactiveAgentsApi.util.updateQueryData(
      "listProactiveAgents",
      args,
      (draft) => {
        draft.proactive_agents = mergeAgentsById(draft.proactive_agents, [
          agent,
        ])
        if (args.created_by === "auto_created" && draft.meta) {
          draft.meta = incrementMetaTotal(draft.meta)
        }
      }
    )
  )
}

function decrementMetaTotal(meta: PagingMeta): PagingMeta {
  const totalCount = Math.max(meta.total_count - 1, 0)
  const totalPages = Math.ceil(totalCount / meta.per_page)
  return {
    ...meta,
    total_count: totalCount,
    total_pages: totalPages,
    next_page: meta.current_page < totalPages ? meta.current_page + 1 : null,
  }
}

function incrementMetaTotal(meta: PagingMeta): PagingMeta {
  const totalCount = meta.total_count + 1
  const totalPages = Math.ceil(totalCount / meta.per_page)
  return {
    ...meta,
    total_count: totalCount,
    total_pages: totalPages,
    next_page: meta.current_page < totalPages ? meta.current_page + 1 : null,
  }
}

// Returns the best matching existing hotel price alert for the given hotel/dates.
// Prefers the most specific match: room-level > hotel-level.
export function useActiveHotelPriceAlert(
  entityRef: string | undefined,
  checkIn: DayObject | undefined,
  checkOut: DayObject | undefined,
  occupancy: HotelPriceAlertOccupancy | undefined,
  hotelRoomEntityRef?: string,
  providerRoomId?: string
): AgentSummary | undefined {
  const { activePriceAlert } = useGetProactiveAgentsQuery(undefined, {
    selectFromResult: ({ data }) => ({
      activePriceAlert: selectExistingHotelPriceAlert(
        data ?? [],
        entityRef,
        checkIn,
        checkOut,
        occupancy,
        hotelRoomEntityRef,
        providerRoomId
      ),
    }),
  })
  return activePriceAlert
}

export type FlightPriceAlertScope = "flight" | "search"

// Returns all overlapping user-created hotel price alerts for the given hotel/dates.
export function useActiveHotelPriceAlertGroup(
  entityRef: string | undefined,
  checkIn: DayObject | undefined,
  checkOut: DayObject | undefined,
  occupancy: HotelPriceAlertOccupancy | undefined
): AgentSummary[] {
  const { activePriceAlertGroup } = useGetProactiveAgentsQuery(undefined, {
    selectFromResult: ({ data }) => ({
      activePriceAlertGroup: selectHotelPriceAlertGroup(
        data ?? [],
        entityRef,
        checkIn,
        checkOut,
        occupancy
      ),
    }),
  })
  return activePriceAlertGroup
}

function watchDatesOverlap(
  existingCheckIn: string,
  existingCheckOut: string,
  checkIn: string,
  checkOut: string
) {
  return existingCheckIn < checkOut && existingCheckOut > checkIn
}

export interface HotelPriceAlertOccupancy {
  children: number
  childAges?: number[]
}

function matchesHotelPriceAlertOccupancy(
  agent: AgentSummary,
  occupancy: HotelPriceAlertOccupancy
): boolean {
  if (agent.agent_type !== "hotel_price_alert") return false

  const config = agent.agent_config
  const configChildAges = config.child_ages ?? []
  const childAges = occupancy.childAges ?? []

  return (
    (config.children ?? 0) === occupancy.children &&
    configChildAges.length === childAges.length &&
    configChildAges.every((age, index) => age === childAges[index])
  )
}

export function hotelPriceAlertCandidates(
  agents: AgentSummary[],
  entityRef: string | undefined,
  checkIn: DayObject | undefined,
  checkOut: DayObject | undefined,
  occupancy: HotelPriceAlertOccupancy | undefined
): AgentSummary[] {
  if (!entityRef || !occupancy) return []

  const checkInStr = checkIn ? dayObjectAsUrlParam(checkIn) : undefined
  const checkOutStr = checkOut ? dayObjectAsUrlParam(checkOut) : undefined

  return agents.filter((agent) => {
    if (!["active", "paused"].includes(agent.status)) return false
    const explicitlyRequested =
      agent.created_by === "user_created" || agent.explicit_request === true
    if (!explicitlyRequested) return false
    const { agent_type, agent_config: cfg } = agent
    if (agent_type !== "hotel_price_alert") return false
    if (cfg.entity_ref !== entityRef) return false
    if (!matchesHotelPriceAlertOccupancy(agent, occupancy)) return false
    if (checkInStr && checkOutStr) {
      if (
        !watchDatesOverlap(cfg.check_in, cfg.check_out, checkInStr, checkOutStr)
      ) {
        return false
      }
    }
    return true
  })
}

export function selectHotelPriceAlertGroup(
  agents: AgentSummary[],
  entityRef: string | undefined,
  checkIn: DayObject | undefined,
  checkOut: DayObject | undefined,
  occupancy: HotelPriceAlertOccupancy | undefined
): AgentSummary[] {
  const candidates = hotelPriceAlertCandidates(
    agents,
    entityRef,
    checkIn,
    checkOut,
    occupancy
  )
  if (candidates.length === 0) return []
  return candidates
}

export function selectExistingHotelPriceAlert(
  agents: AgentSummary[],
  entityRef: string | undefined,
  checkIn: DayObject | undefined,
  checkOut: DayObject | undefined,
  occupancy: HotelPriceAlertOccupancy | undefined,
  hotelRoomEntityRef?: string,
  providerRoomId?: string
): AgentSummary | undefined {
  const candidates = hotelPriceAlertCandidates(
    agents,
    entityRef,
    checkIn,
    checkOut,
    occupancy
  )

  if (candidates.length === 0) return undefined

  // Room-specific button: only light up for an alert that matches this exact room.
  // A hotel-level alert does not count — the user may want to add a room-specific one.
  if (hotelRoomEntityRef || providerRoomId) {
    if (hotelRoomEntityRef) {
      const byRoomRef = candidates.find((agent) => {
        const { agent_type, agent_config: cfg } = agent
        return (
          agent_type === "hotel_price_alert" &&
          cfg.hotel_room_entity_ref === hotelRoomEntityRef
        )
      })
      if (byRoomRef) return byRoomRef
    }
    if (providerRoomId) {
      const byProviderId = candidates.find((agent) => {
        const { agent_type, agent_config: cfg } = agent
        return (
          agent_type === "hotel_price_alert" &&
          cfg.provider_room_id === providerRoomId
        )
      })
      if (byProviderId) return byProviderId
    }
    return undefined
  }

  // Hotel-level button: any matching alert (room-specific or hotel-level) counts.
  return candidates[0]
}
